import React from 'react';

import { useRouter } from 'next/router';
import PropTypes from 'prop-types';
import styled from 'styled-components';

import { createDataAutoTagString } from '~/shared/utils/createDataAutoTagString';
import { getBuilderDataAnalyticsProps } from '~/shared/utils/getBuilderDataAnalyticsProps';
import { Link } from '~/techstyle-shared/next-routes';
import {
  NavLink,
  FABLETICS_BUILDER_ENUMS,
} from '~/techstyle-shared/react-builder-models';

import { useBuilderHamburgerMenuContext } from '../BuilderHamburgerMenu/components/BuilderHamburgerMenuContext';

const NAV_CONTENT_COLORS = FABLETICS_BUILDER_ENUMS.NAV_CONTENT_COLORS;
const CALLOUT_PRIORITIES = NavLink.CALLOUT_PRIORITIES;

const NavLinkWrapper = styled.li`
  list-style: none;
  transition: background-color 0.3s ease;

  ${({ theme }) => theme.builderHeader.navLink.wrapper}
`;

const StyledNavLink = styled.a`
  display: flex;
  align-items: center;
  width: 100%;

  ${({ theme }) => theme.builderHeader.navLink.link}
`;

const AssociatedColor = styled.span`
  background-color: ${({ $color }) => $color};
  border-radius: 50%;

  align-self: center;
  height: 18px;
  width: 18px;
  margin-right: 8px;
`;

const Label = styled.span`
  display: inline-block;
  font-size: 14px;

  ${({ theme }) => theme.builderHeader.navLink.label}

  ${({ $isBold, theme }) => $isBold && theme.builderHeader.navLink.bold}

  ${({ $linkColor, theme }) =>
    $linkColor &&
    $linkColor !== NAV_CONTENT_COLORS.DEFAULT &&
    theme.builderHeader.customColors[$linkColor]};
`;

const LinkCallout = styled.span`
  display: inline-block;

  text-transform: uppercase;

  ${({ theme }) => theme.builderHeader.navLink.callout.default}

  ${({ $customCalloutColor, theme }) =>
    $customCalloutColor &&
    $customCalloutColor !== CALLOUT_PRIORITIES.DEFAULT &&
    theme.builderHeader.navLink.callout.customColors[$customCalloutColor]};
`;

function BuilderNavLink({
  linkText,
  linkUrl,
  isBold,
  linkColor,
  callout,
  associatedColor,
  dataAnalytics,
}) {
  const router = useRouter();
  const closeNav = useBuilderHamburgerMenuContext()?.closeNav;

  const handleSamePathClick = () => {
    if (router.asPath === linkUrl && typeof closeNav === 'function') {
      closeNav();
    }
  };

  const calloutText =
    callout?.hasCallout && callout?.text ? `_Callout-${callout.text}` : '';
  const dataAutotag = dataAnalytics?.label
    ? createDataAutoTagString(dataAnalytics.label)
    : createDataAutoTagString(linkText, calloutText);

  return (
    <NavLinkWrapper
      data-builder-component-type="nav_link"
      data-autotag={dataAutotag}
      {...(dataAnalytics ? getBuilderDataAnalyticsProps(dataAnalytics) : {})}
      onClick={handleSamePathClick}
    >
      <Link href={linkUrl} passHref>
        <StyledNavLink title={linkText}>
          {associatedColor?.hasAssociatedColor && (
            <AssociatedColor $color={associatedColor.color} />
          )}
          <Label $isBold={isBold} $linkColor={linkColor}>
            {linkText}
          </Label>
          {callout?.hasCallout && (
            <LinkCallout $customCalloutColor={callout.backgroundColor}>
              {callout.text}
            </LinkCallout>
          )}
        </StyledNavLink>
      </Link>
    </NavLinkWrapper>
  );
}

BuilderNavLink.propTypes = {
  associatedColor: PropTypes.shape({
    color: PropTypes.string,
    hasAssociatedColor: PropTypes.bool,
  }),
  callout: PropTypes.shape({
    backgroundColor: PropTypes.string,
    hasCallout: PropTypes.bool,
    text: PropTypes.string,
  }),
  dataAnalytics: PropTypes.shape({
    category: PropTypes.string,
    label: PropTypes.string,
  }),
  isBold: PropTypes.bool,
  linkColor: PropTypes.string,
  linkText: PropTypes.string,
  linkUrl: PropTypes.string,
};

export default BuilderNavLink;
